import React, { useEffect, useState, useRef } from 'react';
import styles from '../capitalExpenditure.module.scss';
import { printSummaryTableAsPDF } from '../utils/pdfExport';
import * as XLSX from 'xlsx';

const CapitalExpenditureQ4Report = ({
  displayData = [],
  existingData = [],
  selectedOffice,
  fetchDetailedDataForReports
}) => {
  const [detailedData, setDetailedData] = useState([]);
  const [loadingDetailedData, setLoadingDetailedData] = useState(false);
  const summaryTableRef = useRef(null);
  const [q4Metric, setQ4Metric] = useState('q4_expense');

  useEffect(() => {
    if (fetchDetailedDataForReports && existingData && existingData.length > 0) {
      setLoadingDetailedData(true);
      fetchDetailedDataForReports()
        .then(data => {
          setDetailedData(data);
        })
        .catch(() => {
          setDetailedData(existingData);
        })
        .finally(() => setLoadingDetailedData(false));
    }
  }, [fetchDetailedDataForReports, existingData]);

  const handleDownloadExcel = async () => {
    try {
      const dataForExport = detailedData.length > 0 ? detailedData : existingData;
      const allCapitalItems = [];
      const allOperationalItems = [];

      if (Array.isArray(dataForExport) && dataForExport.length > 0) {
        dataForExport.forEach(expenditure => {
          if (expenditure.expenditure_items && Array.isArray(expenditure.expenditure_items)) {
            const capitalItems = expenditure.expenditure_items
              .filter(item => item.item_type === 'capital' || (item.code_number && item.code_number.startsWith('01.')))
              .map(item => ({
                ...item,
                institution_name: expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                item_type: 'capital'
              }));

            const operationalItems = expenditure.expenditure_items
              .filter(item => item.item_type === 'operational' || (item.code_number && item.code_number.startsWith('02.')))
              .map(item => ({
                ...item,
                institution_name: expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                item_type: 'operational'
              }));

            allCapitalItems.push(...capitalItems);
            allOperationalItems.push(...operationalItems);
          }
        });
      }

      const allExpenditureItems = [...allCapitalItems, ...allOperationalItems];
      const institutions = [...new Set(displayData.map(item => item.institution_name).filter(Boolean))];

      if (allExpenditureItems.length === 0 || institutions.length === 0) {
        alert('कुनै डाटा फेला परेन। कृपया पहिले डाटा लोड गर्नुहोस्।');
        return;
      }

      const allItemsMap = new Map();
      allExpenditureItems.forEach(item => {
        const key = `${item.code_number || ''}-${item.expense_title || ''}-${item.item_type || 'unknown'}`;
        if (!allItemsMap.has(key)) {
          allItemsMap.set(key, {
            code_number: item.code_number || '',
            expense_title: item.expense_title || '',
            item_type: item.item_type || 'unknown',
            institutions: {},
            total: 0
          });
        }
        const itemData = allItemsMap.get(key);
        const camelMetric = q4Metric.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        const amount = parseFloat((item[q4Metric] ?? item[camelMetric] ?? 0));
        const institutionName = item.institution_name || 'Unknown';
        if (!itemData.institutions[institutionName]) {
          itemData.institutions[institutionName] = 0;
        }
        itemData.institutions[institutionName] += amount;
        itemData.total += amount;
      });

      const excelData = [];
      const headers = ['बजेट कोड', 'बजेट शिर्षक', ...institutions, 'जम्मा'];
      excelData.push(headers);

      const entries = Array.from(allItemsMap.entries());
      const capitalEntries = entries.filter(([_, v]) => v.item_type === 'capital');
      const operationalEntries = entries.filter(([_, v]) => v.item_type === 'operational');

      const capitalTotals = institutions.map(inst =>
        capitalEntries.reduce((sum, [_, v]) => sum + Number(v.institutions[inst] || 0), 0)
      );
      const operationalTotals = institutions.map(inst =>
        operationalEntries.reduce((sum, [_, v]) => sum + Number(v.institutions[inst] || 0), 0)
      );
      const capitalGrand = capitalTotals.reduce((a, b) => a + b, 0);
      const operationalGrand = operationalTotals.reduce((a, b) => a + b, 0);

      excelData.push(['', 'पुँजीगत खर्च जम्मा', ...capitalTotals, capitalGrand]);

      capitalEntries.forEach(([_, itemData]) => {
        const row = [itemData.code_number, itemData.expense_title];
        institutions.forEach(inst => {
          row.push(Number(itemData.institutions[inst] || 0));
        });
        row.push(Number(itemData.total || 0));
        excelData.push(row);
      });

      excelData.push(['', '', ...institutions.map(() => ''), '']);

      excelData.push(['', 'संचालन खर्च जम्मा', ...operationalTotals, operationalGrand]);

      operationalEntries.forEach(([_, itemData]) => {
        const row = [itemData.code_number, itemData.expense_title];
        institutions.forEach(inst => {
          row.push(Number(itemData.institutions[inst] || 0));
        });
        row.push(Number(itemData.total || 0));
        excelData.push(row);
      });

      excelData.push(['', 'जम्मा खर्च', ...institutions.map((_, i) => Number(capitalTotals[i] + operationalTotals[i] || 0)), Number(capitalGrand + operationalGrand)]);

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'चौथो त्रैमासिक खर्च');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `चौथो_त्रैमासिक_खर्च_${timestamp}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      alert(`Excel फाइल सफलतापूर्वक डाउनलोड भयो: ${fileName}`);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      alert('Excel डाउनलोडमा समस्या भयो।');
    }
  };

  const handlePrintSummaryTable = async () => {
    try {
      if (summaryTableRef.current) {
        const result = await printSummaryTableAsPDF(summaryTableRef.current, 'खर्च_सारांश_तालिका');
        if (!result.success) {
          alert(`प्रिन्टमा समस्या भयो: ${result.error}`);
        }
      } else {
        alert('तालिका फेला परेन। कृपया पहिले विस्तृत रिपोर्ट खोल्नुहोस्।');
      }
    } catch (error) {
      console.error('Error printing:', error);
      alert('प्रिन्टमा समस्या भयो।');
    }
  };

  return (
    <div className={styles.detailedReportSection}>
      <div className={styles.detailedHeader}>
        <h3>📋 विस्तृत रिपोर्ट</h3>
        <p>चौथो त्रैमासिकको खर्च आइटमहरूको विस्तृत विवरण</p>
      </div>

      {/* Q4 Metric Filter */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
        <label htmlFor="q4MetricSelect">चौथो त्रैमासिक फिल्टर:</label>
        <select
          id="q4MetricSelect"
          value={q4Metric}
          onChange={(e) => setQ4Metric(e.target.value)}
          style={{ padding: '4px 8px' }}
        >
          <option value="q4_budget_demand">बजेट माग</option>
          <option value="q4_budget_expenditure">बजेट निकासा</option>
          <option value="q4_authority_received">अख्तियारी प्राप्त</option>
          <option value="q4_expense">खर्च</option>
        </select>
      </div>

      <div className={styles.detailedReportTabs}>
        <button 
          className={`${styles.detailedTabButton} ${styles.activeDetailedTab}`}
          title="विस्तृत रिपोर्ट देखाउनुहोस्"
        >
          📋 विस्तृत रिपोर्ट
        </button>
        <button 
          className={styles.detailedTabButton}
          onClick={handleDownloadExcel}
          title="Excel मा डाउनलोड गर्नुहोस्"
        >
          📊 Excel डाउनलोड
        </button>
        <button 
          className={styles.detailedTabButton}
          onClick={handlePrintSummaryTable}
          title="प्रिन्ट गर्नुहोस्"
        >
          🖨️ प्रिन्ट
        </button>
      </div>

      <div className={styles.summaryExcelTableContainer}>
        <h4 style={{ textAlign: 'left' }}>खर्च सारांश तालिका (चौथो त्रैमासिक)</h4>
        {loadingDetailedData ? (
          <div className={styles.loading}>
            <p>विस्तृत डाटा लोड हुँदैछ...</p>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.summaryExcelTable} ref={summaryTableRef}>
              <thead>
                <tr>
                  <th className={styles.budgetCodeHeader}>बजेट कोड</th>
                  <th className={styles.budgetTitleHeader}>बजेट शिर्षक</th>
                  {(() => {
                    let institutions = [...new Set(displayData.map(item => item.institution_name).filter(Boolean))];
                    if (institutions.length === 0) {
                      const dataForInst = (detailedData.length > 0 ? detailedData : existingData) || [];
                      const instSet = new Set();
                      dataForInst.forEach(expenditure => {
                        const name = expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय';
                        if (name) instSet.add(name);
                      });
                      institutions = Array.from(instSet);
                    }
                    return institutions.map((name, i) => (
                      <th key={i} className={styles.institutionHeader}>
                        {name}
                      </th>
                    ));
                  })()}
                  <th className={styles.totalHeader}>जम्मा</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const allItemsMap = new Map();
                  const allCapitalItems = [];
                  const allOperationalItems = [];

                  const dataForExport = detailedData.length > 0 ? detailedData : existingData;

                  if (Array.isArray(dataForExport) && dataForExport.length > 0) {
                    dataForExport.forEach(expenditure => {
                      if (expenditure.expenditure_items && Array.isArray(expenditure.expenditure_items)) {
                        const capitalItems = expenditure.expenditure_items
                          .filter(item => item.item_type === 'capital' || (item.code_number && item.code_number.startsWith('01.')))
                          .map(item => ({
                            ...item,
                            institution_name: expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                            item_type: 'capital'
                          }));

                        const operationalItems = expenditure.expenditure_items
                          .filter(item => item.item_type === 'operational' || (item.code_number && item.code_number.startsWith('02.')))
                          .map(item => ({
                            ...item,
                            institution_name: expenditure.institution_name || selectedOffice?.name || 'केन्द्रीय कार्यालय',
                            item_type: 'operational'
                          }));

                        allCapitalItems.push(...capitalItems);
                        allOperationalItems.push(...operationalItems);
                      }
                    });
                  }

                  const allExpenditureItems = [...allCapitalItems, ...allOperationalItems];

                  if (allExpenditureItems.length === 0) {
                    return (
                      <tr>
                        <td colSpan={22} style={{ textAlign: 'center', padding: '20px' }}>
                          कुनै डाटा उपलब्ध छैन।
                        </td>
                      </tr>
                    );
                  }

                  const institutionSet = new Set();
                  allExpenditureItems.forEach(item => {
                    const key = `${item.code_number || ''}-${item.expense_title || ''}-${item.item_type || 'unknown'}`;
                    if (!allItemsMap.has(key)) {
                      allItemsMap.set(key, {
                        code_number: item.code_number || '',
                        expense_title: item.expense_title || item.title || '',
                        item_type: item.item_type || 'unknown',
                        institutions: {},
                        total: 0
                      });
                    }
                    const itemData = allItemsMap.get(key);
                    const camelMetric = q4Metric.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
                    const amount = parseFloat((item[q4Metric] ?? item[camelMetric] ?? 0));
                    const institutionName = item.institution_name || 'Unknown';
                    if (!itemData.institutions[institutionName]) {
                      itemData.institutions[institutionName] = 0;
                    }
                    itemData.institutions[institutionName] += amount;
                    itemData.total += amount;
                    if (institutionName) institutionSet.add(institutionName);
                  });

                  let institutionList = Array.from(institutionSet);
                  if (institutionList.length === 0) {
                    institutionList = [...new Set(displayData.map(item => item.institution_name).filter(Boolean))];
                  }

                  const rows = [];
                  const entries = Array.from(allItemsMap.entries());
                  const capitalEntries = entries.filter(([_, v]) => v.item_type === 'capital');
                  const operationalEntries = entries.filter(([_, v]) => v.item_type === 'operational');

                  const capitalTotals = institutionList.map(inst =>
                    capitalEntries.reduce((sum, [_, v]) => sum + Number(v.institutions[inst] || 0), 0)
                  );
                  const operationalTotals = institutionList.map(inst =>
                    operationalEntries.reduce((sum, [_, v]) => sum + Number(v.institutions[inst] || 0), 0)
                  );
                  const capitalGrand = capitalTotals.reduce((a, b) => a + b, 0);
                  const operationalGrand = operationalTotals.reduce((a, b) => a + b, 0);

                  rows.push(
                    <tr key="capital-total" className={`capital-total`}>
                      <td className={styles.budgetCodeCell} style={{ textAlign: 'left' }}></td>
                      <td className={styles.budgetTitleCell} style={{ textAlign: 'left', fontWeight: 'bold' }}>पुँजीगत खर्च जम्मा</td>
                      {institutionList.map((inst, idx) => (
                        <td key={idx} className={styles.institutionCell} style={{ textAlign: 'left' }}>
                          {Number(capitalTotals[idx] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      ))}
                      <td className={`${styles.totalCell} total-column`} style={{ textAlign: 'left' }}>
                        {Number(capitalGrand || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );

                  capitalEntries.forEach(([key, itemData]) => {
                    rows.push(
                      <tr key={key}>
                        <td className={styles.budgetCodeCell} style={{ textAlign: 'left' }}>{itemData.code_number}</td>
                        <td className={styles.budgetTitleCell} style={{ textAlign: 'left' }}>{itemData.expense_title}</td>
                        {institutionList.map((institution, idx) => (
                          <td key={idx} className={styles.institutionCell} style={{ textAlign: 'left' }}>
                            {Number(itemData.institutions[institution] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                        ))}
                        <td className={styles.totalCell} style={{ textAlign: 'left' }}>
                          {Number(itemData.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  });

                  rows.push(
                    <tr key="operational-total" className={`operational-total`}>
                      <td className={styles.budgetCodeCell} style={{ textAlign: 'left' }}></td>
                      <td className={styles.budgetTitleCell} style={{ textAlign: 'left', fontWeight: 'bold' }}>संचालन खर्च जम्मा</td>
                      {institutionList.map((inst, idx) => (
                        <td key={idx} className={styles.institutionCell} style={{ textAlign: 'left' }}>
                          {Number(operationalTotals[idx] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      ))}
                      <td className={`${styles.totalCell} total-column`} style={{ textAlign: 'left' }}>
                        {Number(operationalGrand || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );

                  operationalEntries.forEach(([key, itemData]) => {
                    rows.push(
                      <tr key={key}>
                        <td className={styles.budgetCodeCell} style={{ textAlign: 'left' }}>{itemData.code_number}</td>
                        <td className={styles.budgetTitleCell} style={{ textAlign: 'left' }}>{itemData.expense_title}</td>
                        {institutionList.slice(0, 20).map((institution, idx) => (
                          <td key={idx} className={styles.institutionCell} style={{ textAlign: 'left' }}>
                            {Number(itemData.institutions[institution] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>
                        ))}
                        <td className={styles.totalCell} style={{ textAlign: 'left' }}>
                          {Number(itemData.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  });

                  rows.push(
                    <tr key="grand-total" className={`grand-total`}>
                      <td className={styles.budgetCodeCell} style={{ textAlign: 'left' }}></td>
                      <td className={styles.budgetTitleCell} style={{ textAlign: 'left' }}>जम्मा खर्च</td>
                      {institutionList.map((inst, idx) => (
                        <td key={idx} className={styles.institutionCell} style={{ textAlign: 'left' }}>
                          {Number((capitalTotals[idx] || 0) + (operationalTotals[idx] || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      ))}
                      <td className={`${styles.totalCell} total-column`} style={{ textAlign: 'left' }}>
                        {Number((capitalGrand || 0) + (operationalGrand || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );

                  return rows;
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapitalExpenditureQ4Report;
